import { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { t, getLocale } from '@/i18n';
import { HelpTip } from '@/components/ui/help-tip';

const fmt = (n, digits = 0) => (n == null ? '—' : n.toLocaleString(getLocale(), { maximumFractionDigits: digits }));

// Columns: key in the aggregate, unit, and whether a lower value is better.
const columns = [
  { key: 'loadings', unit: '' },
  { key: 'announcedPct', unit: ' %' },
  { key: 'noShowPct', unit: ' %', lowerIsBetter: true, alarm: 15 },
  { key: 'outOfHours', unit: '', lowerIsBetter: true },
  { key: 'replyMedian', unit: ' min', lowerIsBetter: true },
  { key: 'onTimePct', unit: ' %' },
];

const Trend = ({ now, prev, lowerIsBetter }) => {
  if (prev == null || now == null || Math.round(now) === Math.round(prev)) return null;
  const better = lowerIsBetter ? now < prev : now > prev;
  const Icon = now < prev ? ArrowDown : ArrowUp;
  return <Icon className={cn('inline h-3 w-3 ml-0.5', better ? 'text-emerald-600' : 'text-red-600')} />;
};

export const CarrierScorecard = ({ rows, onSelect }) => {
  const [sort, setSort] = useState({ key: 'noShowPct', desc: true });
  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const d = (a.now[sort.key] ?? -1) - (b.now[sort.key] ?? -1);
    return sort.desc ? -d : d;
  }), [rows, sort]);

  return (
    <Card className="p-0 overflow-hidden" data-tour="stats-carriers">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs text-muted-foreground">
            <tr>
              <th className="text-left font-medium px-4 py-2">{t('stats.carrier.name')}</th>
              {columns.map(c => (
                <th key={c.key} className="text-right font-medium px-3 py-2 align-bottom">
                  <button
                    className={cn('hover:text-foreground', sort.key === c.key && 'text-foreground font-semibold')}
                    onClick={() => setSort(s => ({ key: c.key, desc: s.key === c.key ? !s.desc : true }))}
                  >
                    {t(`stats.carrier.${c.key}`)}{sort.key === c.key && (sort.desc ? ' ↓' : ' ↑')}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(({ carrier, now, prev }) => (
              <tr key={carrier.id} className="border-t hover:bg-muted/40 cursor-pointer" onClick={() => onSelect(carrier.id)}>
                <td className="px-4 py-2.5">
                  <div className="font-medium whitespace-nowrap">{carrier.name}</div>
                  <div className="text-[11px] text-muted-foreground">{carrier.country} · {carrier.fleet} {t('stats.carrier.trucks')}</div>
                </td>
                {columns.map(c => (
                  <td
                    key={c.key}
                    className={cn('px-3 py-2.5 text-right tabular-nums whitespace-nowrap',
                      c.alarm && now[c.key] >= c.alarm && 'text-red-700 font-semibold')}
                  >
                    {fmt(now[c.key])}{now[c.key] != null && c.unit}
                    <Trend now={now[c.key]} prev={prev?.[c.key]} lowerIsBetter={c.lowerIsBetter} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 text-[11px] text-muted-foreground border-t flex items-center gap-1"><HelpTip id="carriers" side="top" />{t('stats.carrier.legend')}</div>
    </Card>
  );
};
